import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getMessaging, getToken, isSupported, onMessage, type Messaging } from 'firebase/messaging'
import { getFirebaseClientConfig, isFirebaseClientConfigured } from '@/config/firebase'
import { profileService } from '@/services/profile.service'
import router from '@/router'
import { toTrustedAppUrl } from '@/utils/sanitizeUrl'

let app: FirebaseApp | null = null
let messaging: Messaging | null = null
let foregroundListenerAttached = false

async function getMessagingInstance(): Promise<Messaging | null> {
  if (!isFirebaseClientConfigured()) {
    return null
  }
  const supported = await isSupported()
  if (!supported) {
    return null
  }
  const config = getFirebaseClientConfig()
  if (!config) {
    return null
  }
  if (!app) {
    app = getApps().length ? getApps()[0]! : initializeApp({
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
    })
  }
  if (!messaging) {
    messaging = getMessaging(app)
  }
  return messaging
}

function showLocalNotification(title: string, body: string, url?: string) {
  if (Notification.permission !== 'granted') {
    return
  }
  const notification = new Notification(title, {
    body,
    icon: '/icons/icon-192.png',
    data: url ? { url } : undefined,
  })
  notification.onclick = () => {
    window.focus()
    const target = url ? toTrustedAppUrl(url, window.location.origin) : null
    if (target) {
      // The tab that owns this notification is already open, so route in-app
      // instead of reloading the whole application.
      const { pathname, search, hash } = new URL(target)
      void router.push(`${pathname}${search}${hash}`)
    }
    notification.close()
  }
}

/** Listen for FCM while the app tab is focused (SW handles background only). */
export async function setupForegroundPushListener(): Promise<void> {
  if (foregroundListenerAttached || Notification.permission !== 'granted') {
    return
  }
  const config = getFirebaseClientConfig()
  if (!config) {
    return
  }
  await ensureServiceWorker(config)
  const messagingInstance = await getMessagingInstance()
  if (!messagingInstance) {
    return
  }
  onMessage(messagingInstance, (payload) => {
    const title = payload.notification?.title ?? 'Notification'
    const body = payload.notification?.body ?? ''
    const url = payload.data?.url
    showLocalNotification(title, body, url)
  })
  foregroundListenerAttached = true
}

async function ensureServiceWorker(config: NonNullable<ReturnType<typeof getFirebaseClientConfig>>) {
  if (!('serviceWorker' in navigator)) {
    return null
  }

  if (import.meta.env.VITE_ENABLE_PWA === 'true') {
    return navigator.serviceWorker.ready
  }

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
  await navigator.serviceWorker.ready
  const target = registration.active || registration.waiting || registration.installing
  target?.postMessage({ type: 'INIT_FIREBASE', config })
  return registration
}

export async function registerPushNotifications(): Promise<string | null> {
  const config = getFirebaseClientConfig()
  if (!config) {
    return null
  }

  const messagingInstance = await getMessagingInstance()
  if (!messagingInstance) {
    return null
  }

  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return null
    }
  } else if (Notification.permission !== 'granted') {
    return null
  }

  const swRegistration = await ensureServiceWorker(config)
  const token = await getToken(messagingInstance, {
    vapidKey: config.vapidKey,
    serviceWorkerRegistration: swRegistration ?? undefined,
  })
  if (!token) {
    return null
  }

  await profileService.registerPushToken(token)
  await setupForegroundPushListener()
  return token
}

export async function unregisterPushNotifications(): Promise<void> {
  await profileService.unregisterPushToken()
}

export function isPushAvailable(): boolean {
  return isFirebaseClientConfigured() && typeof Notification !== 'undefined'
}
